/**
 * Doctor Inbox — relays + confirmed handoffs from SoulAI (PDF §A.2, A.6)
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Inbox, Send, CheckCircle } from 'lucide-react';

export default function DoctorInbox() {
  const [relays, setRelays] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [r, h] = await Promise.all([
        api.get('/api/doctor-relay/inbox'),
        api.get('/api/soulai/handoffs/doctor/inbox'),
      ]);
      setRelays(r.data?.relays || []);
      setHandoffs(h.data?.handoffs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const sendReply = async (relay_id) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/api/doctor-relay/inbox/${relay_id}/reply`, { content: replyText.trim() });
      toast.success('Reply sent — patient will see it in SoulAI');
      setReplyText('');
      loadAll();
      setSelected(null);
    } catch {
      toast.error('Could not send reply');
    }
  };

  const resolve = async (relay_id) => {
    try {
      await api.post(`/api/doctor-relay/inbox/${relay_id}/resolve`, {});
      toast.success('Marked resolved');
      loadAll();
    } catch {
      toast.error('Could not mark resolved');
    }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="doctor-inbox">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl">Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Patient relays routed by SoulAI · Confirmed handoffs
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-1 text-amber-600">
            <Inbox className="h-4 w-4" />
            <span data-testid="inbox-pending-count">{relays.filter(r => r.status === 'pending').length}</span>
            <span>pending</span>
          </div>
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6">
        {/* List */}
        <div className="space-y-4" data-testid="inbox-list">
          <Section title="Active Relays" testId="relays-section">
            {relays.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No relays yet.</p>
            )}
            {relays.map(r => (
              <InboxRow
                key={r.id}
                active={selected?.id === r.id}
                onClick={() => setSelected({ type: 'relay', ...r })}
                title={r.patient_name}
                subtitle={r.chief_concern}
                status={r.status}
                timestamp={r.created_at}
                testId={`relay-${r.id}`}
              />
            ))}
          </Section>

          {handoffs.length > 0 && (
            <Section title="Handoffs (with clinical brief)" testId="handoffs-section">
              {handoffs.map(h => (
                <InboxRow
                  key={h.id}
                  active={selected?.id === h.id}
                  onClick={() => setSelected({ type: 'handoff', ...h })}
                  title={h.patient_name}
                  subtitle={h.route_reason || `Routed to ${h.route_to}`}
                  status={h.status}
                  timestamp={h.created_at}
                  testId={`handoff-${h.id}`}
                />
              ))}
            </Section>
          )}
        </div>

        {/* Detail */}
        <Card className="p-6" data-testid="inbox-detail">
          {!selected ? (
            <div className="text-center py-12">
              <Inbox className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground">Select a relay or handoff to view details.</p>
            </div>
          ) : selected.type === 'handoff' ? (
            <HandoffDetail h={selected} />
          ) : (
            <RelayDetail
              r={selected}
              replyText={replyText}
              setReplyText={setReplyText}
              onReply={() => sendReply(selected.id)}
              onResolve={() => resolve(selected.id)}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </Card>
  );
}

function InboxRow({ active, onClick, title, subtitle, status, timestamp, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${
        active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium truncate">{title}</span>
        <Badge
          variant={status === 'pending' ? 'default' : 'secondary'}
          className={status === 'pending' ? 'bg-amber-500' : ''}
        >
          {status}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
      <p className="text-xs text-muted-foreground/80 mt-1">
        {new Date(timestamp).toLocaleString()}
      </p>
    </button>
  );
}

function RelayDetail({ r, replyText, setReplyText, onReply, onResolve }) {
  return (
    <div className="space-y-4" data-testid="relay-detail">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Patient</p>
        <p className="font-medium">{r.patient_name}</p>
        <p className="text-sm text-muted-foreground">{r.patient_email}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Chief concern</p>
        <p>{r.chief_concern}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Summary</p>
        <p className="text-sm whitespace-pre-wrap">{r.symptom_summary}</p>
      </div>
      {r.triage_tier && (
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Triage</p>
          <Badge>{r.triage_tier}</Badge>
        </div>
      )}

      {(r.messages || []).length > 0 && (
        <>
          <Separator />
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Conversation</p>
            {r.messages.map((m, i) => (
              <div key={i} className="py-2 border-b last:border-0">
                <p className="text-xs text-muted-foreground mb-1">
                  {m.sender_type === 'doctor' ? 'You' : r.patient_name} ·{' '}
                  {new Date(m.timestamp).toLocaleString()}
                </p>
                <p className="text-sm">{m.content}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {r.status !== 'resolved' && (
        <>
          <Separator />
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Reply</p>
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              placeholder="Your reply will be delivered to the patient inside their SoulAI chat."
              data-testid="reply-textarea"
            />
            <div className="flex gap-2 mt-3">
              <Button onClick={onReply} disabled={!replyText.trim()} data-testid="send-reply-button">
                <Send className="h-4 w-4 mr-2" /> Send reply
              </Button>
              <Button variant="outline" onClick={onResolve} data-testid="resolve-button">
                <CheckCircle className="h-4 w-4 mr-2" /> Mark resolved
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HandoffDetail({ h }) {
  const md = h.summary_md_edited || h.summary_md || '—';
  return (
    <div className="space-y-4" data-testid="handoff-detail">
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Patient</p>
        <p className="font-medium">{h.patient_name}</p>
        <p className="text-sm text-muted-foreground">{h.patient_email}</p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Clinical brief</p>
        <pre className="text-sm whitespace-pre-wrap font-sans bg-muted/30 p-4 rounded-lg">{md}</pre>
      </div>
    </div>
  );
}
